// ギフティング画面の純関数ルール（Step 0 で凍結・scripts/test-gifting-rules.mjs で検証）。
// 設計正本=tmp/fable/2026-10-05-gifting-console/plan.md §2-1・§3-3・§3-4。
// サーバー側の正本は gifting-harness src/lib/stage.ts（段階遷移）。ここは表示・判定用の写し。
import type {
  AffAffiliate,
  GfAutoDm,
  GfCampaign,
  GfCampaignType,
  GfCreator,
  GfDealStatus,
  GfEntry,
  GfIgDetectedMedia,
  GfIgDetectStatus,
  GfPlatform,
  GfPost,
  GfReuseState,
  GfSns,
  GfStage,
  GfStageSource,
} from "../types/gifting";

/** 第1弾=1（出荷は手動で追跡入力）。第2弾=2（住所受領→「出荷指示を送る」1クリック→追跡は同期で取り込む）。 */
export const GF_PHASE: 1 | 2 = 2;

export const GF_ORIGIN = "https://gift.nayu.site";

// ---- 段階 -----------------------------------------------------------------------

export const STAGE_NAMES: readonly string[] = [
  "リスト化",
  "打診中",
  "返信待ち",
  "条件合意",
  "住所回収",
  "発送済",
  "投稿確認",
  "2次利用許諾",
  "アーカイブ",
];

/** 7セグメント進捗バーの対象段階（1〜7） */
export const BAR_STAGES: readonly GfStage[] = [1, 2, 3, 4, 5, 6, 7];

/** パネルのタイムラインで「未到達の自動段階」に出す出所 */
export const STAGE_AUTO_SOURCE: Partial<Record<GfStage, string>> = {
  4: "本人フォーム",
  5: GF_PHASE >= 2 ? "OMS 追跡" : "",
  6: "本人フォーム",
};

export const SOURCE_LABEL: Record<GfStageSource, string> = {
  manual: "手動",
  form: "本人フォーム",
  oms: "OMS 追跡",
  ig_api: "Instagram API",
  system: "システム",
  legacy: "移行",
};

export interface BoardColumnDef {
  key: "nego" | "ship" | "post";
  name: string;
  sub: string;
  range: [GfStage, GfStage];
  waitLabel: string;
}

export const BOARD_COLUMNS: readonly BoardColumnDef[] = [
  { key: "nego", name: "交渉中", sub: "打診 → 返信 → 条件合意", range: [1, 3], waitLabel: "返信待ち" },
  {
    key: "ship",
    name: "配送中",
    sub: GF_PHASE >= 2 ? "住所回収 → 発送（フォーム・OMS連動）" : "住所回収 → 発送（フォーム連動）",
    range: [4, 5],
    waitLabel: "進行中",
  },
  { key: "post", name: "投稿・許諾", sub: "投稿 → 確認 → 2次利用許諾", range: [6, 7], waitLabel: "投稿待ち" },
];

/** アーカイブ扱い（カンバン・一覧から外す）: 段階8 または中止 */
export function isArchived(e: Pick<GfEntry, "stage" | "status">): boolean {
  return e.stage >= 8 || e.status === "cancelled";
}

/**
 * status 直書き時の逆同期（plan.md §2-1）。サーバー stage.ts と同じ規則。
 * draft→min(cur,3)、waiting_address/address_received→4、shipped→5、posted→cur>=6?cur:6、completed→8、cancelled→cur
 */
export function stageFromStatus(status: GfDealStatus, cur: GfStage): GfStage {
  switch (status) {
    case "draft":
      return (cur >= 1 && cur <= 3 ? cur : 3) as GfStage;
    case "waiting_address":
    case "address_received":
      return 4;
    case "shipped":
      return 5;
    case "posted":
      return cur >= 6 && cur <= 7 ? cur : 6;
    case "completed":
      return 8;
    case "cancelled":
      return cur;
  }
}

/** 段階→派生 status（段階4 の副状態は呼び出し側が渡す） */
export function statusForStage(stage: GfStage, addressReceived = false): GfDealStatus {
  if (stage <= 3) return "draft";
  if (stage === 4) return addressReceived ? "address_received" : "waiting_address";
  if (stage === 5) return "shipped";
  if (stage <= 7) return "posted";
  return "completed";
}

/** 段階を進めた先（6→8 直行は reuse_required=0）。7/8 以外の上限は8 */
export function nextStage(e: Pick<GfEntry, "stage">, campaign: Pick<GfCampaign, "reuse_required"> | null): GfStage {
  if (e.stage === 6 && campaign && campaign.reuse_required === 0) return 8;
  return Math.min(e.stage + 1, 8) as GfStage;
}

// ---- 時刻・経過日数 ------------------------------------------------------------------

const DAY_MS = 86_400_000;

/** サーバーの時刻文字列を Date に。'YYYY-MM-DD HH:MM:SS'（SQLite datetime・UTC）と ISO の両方を受ける */
export function parseServerTime(s: string | null | undefined): Date | null {
  if (!s) return null;
  let t = s.trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(t)) t += "T00:00:00";
  else if (!t.includes("T")) t = t.replace(" ", "T");
  if (!/([zZ]|[+-]\d\d:?\d\d)$/.test(t)) t += "Z";
  const d = new Date(t);
  return Number.isNaN(d.getTime()) ? null : d;
}

/**
 * 経過日数 = floor((now − 基準)/1日)。基準 = stage_changed_at と（段階1〜3なら）last_contact_at の新しい方。
 * 基準が無ければ created_at。
 */
export function ageDays(
  e: Pick<GfEntry, "stage" | "stage_changed_at" | "last_contact_at" | "created_at">,
  nowIso: string
): number {
  const now = parseServerTime(nowIso)?.getTime() ?? Date.now();
  const cands = [parseServerTime(e.stage_changed_at)];
  if (e.stage >= 1 && e.stage <= 3) cands.push(parseServerTime(e.last_contact_at));
  const base = cands.filter((d): d is Date => !!d).reduce<number | null>((m, d) => (m === null || d.getTime() > m ? d.getTime() : m), null)
    ?? parseServerTime(e.created_at)?.getTime()
    ?? now;
  return Math.max(0, Math.floor((now - base) / DAY_MS));
}

export function ageLabel(days: number): string {
  return days === 0 ? "今日" : `${days}日経過`;
}

/** 4日以上は赤（段階7以上は除く） */
export function ageIsLate(days: number, stage: GfStage): boolean {
  return days >= 4 && stage < 7;
}

const JST_FMT_MD = new Intl.DateTimeFormat("ja-JP", { timeZone: "Asia/Tokyo", month: "numeric", day: "numeric" });
const JST_FMT_YMD = new Intl.DateTimeFormat("en-CA", { timeZone: "Asia/Tokyo" });
const JST_FMT_DT = new Intl.DateTimeFormat("ja-JP", {
  timeZone: "Asia/Tokyo",
  month: "numeric",
  day: "numeric",
  hour: "2-digit",
  minute: "2-digit",
});

/** 'M/D'（JST）。無ければ '' */
export function fmtMD(s: string | null | undefined): string {
  const d = parseServerTime(s);
  if (!d) return "";
  const parts = JST_FMT_MD.formatToParts(d);
  const m = parts.find((p) => p.type === "month")?.value ?? "";
  const day = parts.find((p) => p.type === "day")?.value ?? "";
  return `${m}/${day.padStart(2, "0")}`;
}

/** 'YYYY-MM-DD'（JST） */
export function fmtYmd(s: string | null | undefined): string {
  const d = parseServerTime(s);
  return d ? JST_FMT_YMD.format(d) : "";
}

/** 'M/D HH:MM'（JST） */
export function fmtDateTime(s: string | null | undefined): string {
  const d = parseServerTime(s);
  return d ? JST_FMT_DT.format(d) : "";
}

/** 'YYYY-MM'（JST） */
export function jstMonthOf(nowIso: string): string {
  const d = parseServerTime(nowIso) ?? new Date();
  return JST_FMT_YMD.format(d).slice(0, 7);
}

/** 'YYYY年M月D日（曜）時点' */
export function fmtAsOf(nowIso: string): string {
  const d = parseServerTime(nowIso) ?? new Date();
  const s = new Intl.DateTimeFormat("ja-JP", {
    timeZone: "Asia/Tokyo",
    year: "numeric",
    month: "long",
    day: "numeric",
    weekday: "short",
  }).format(d);
  return `${s.replace(/\((.)\)/, "（$1）")}時点`;
}

// ---- 要対応判定 ----------------------------------------------------------------------

export type ActionTone = "warn" | "caution" | "info" | "ok";

export interface EntryAction {
  /** カード右上の短いラベル（例: 催促） */
  label: string;
  /** パネル主ボタンの文言 */
  cta: string;
  tone: ActionTone;
}

/**
 * 要対応なら EntryAction、待ちなら null（plan.md §3-4）。
 * 段階1・3・6・7=常に要対応。段階2 age≥4→催促。段階4 waiting_address age≥3→住所を催促。
 * 段階4 address_received & 出荷未指示→発送する（第2弾: 出荷指示を送る／検証NGなら住所の書式を修正）。
 * 段階5→待ち（post_deadline 超過で「投稿を催促」）。
 */
export function entryAction(
  e: GfEntry,
  campaign: Pick<GfCampaign, "reuse_required"> | null,
  nowIso: string
): EntryAction | null {
  if (isArchived(e)) return null;
  const age = ageDays(e, nowIso);
  switch (e.stage) {
    case 1:
      return { label: "打診する", cta: "打診した（記録）", tone: "info" };
    case 2:
      return age >= 4 ? { label: "催促", cta: "催促した（記録）", tone: "warn" } : null;
    case 3:
      return { label: "条件を確定", cta: "住所フォームを送る", tone: "info" };
    case 4:
      if (e.status === "address_received") {
        if (e.ship_order_no) return null;
        if (GF_PHASE >= 2) {
          if (e.ship_validation && !e.ship_validation.ok) return { label: "住所を修正", cta: "住所の書式を修正", tone: "warn" };
          return { label: "出荷指示", cta: "出荷指示を送る", tone: "info" };
        }
        return { label: "発送する", cta: "発送済みにする", tone: "info" };
      }
      return age >= 3 ? { label: "住所を催促", cta: "住所を催促した（記録）", tone: "caution" } : null;
    case 5: {
      const dl = e.post_deadline ? parseServerTime(e.post_deadline.length === 10 ? `${e.post_deadline}T23:59:59+09:00` : e.post_deadline) : null;
      const now = parseServerTime(nowIso);
      if (dl && now && now.getTime() > dl.getTime()) return { label: "投稿を催促", cta: "投稿を記録する", tone: "warn" };
      return null;
    }
    case 6:
      return campaign && campaign.reuse_required === 0
        ? { label: "確認して完了", cta: "投稿を確認してアーカイブ", tone: "info" }
        : { label: "投稿を確認", cta: "投稿を確認する", tone: "info" };
    case 7:
      return { label: "許諾を記録", cta: "2次利用許諾を記録してアーカイブ", tone: "info" };
    default:
      return null;
  }
}

/** 待ちカードやパネルで使う「次にやること」の文言（要対応でない時も出す） */
export function nextLabel(e: GfEntry, campaign: Pick<GfCampaign, "reuse_required"> | null, nowIso: string): string {
  const a = entryAction(e, campaign, nowIso);
  if (a) return a.cta;
  if (e.status === "cancelled") return "中止";
  switch (e.stage) {
    case 2:
      return "返信を待つ";
    case 4:
      return e.status === "address_received" ? "出荷指示済・追跡待ち" : "住所の入力を待つ";
    case 5:
      return "投稿を待つ";
    case 8:
      return "完了";
    default:
      return "";
  }
}

export interface BoardCard {
  entry: GfEntry;
  action: EntryAction | null;
  age: number;
}

export interface BoardColumn extends BoardColumnDef {
  act: BoardCard[];
  wait: BoardCard[];
  count: number;
}

/** カンバン列の組み立て。要対応は経過日数の降順、待ちは段階昇順（同段階は経過降順） */
export function buildBoardColumns(
  entries: GfEntry[],
  campaignsById: Map<string, GfCampaign>,
  nowIso: string,
  campaignFilter: string
): BoardColumn[] {
  const live = entries.filter((e) => !isArchived(e) && (campaignFilter === "all" || e.campaign_id === campaignFilter));
  return BOARD_COLUMNS.map((col) => {
    const cards = live
      .filter((e) => e.stage >= col.range[0] && e.stage <= col.range[1])
      .map<BoardCard>((e) => {
        const c = e.campaign_id ? campaignsById.get(e.campaign_id) ?? null : null;
        return { entry: e, action: entryAction(e, c, nowIso), age: ageDays(e, nowIso) };
      });
    const act = cards.filter((c) => c.action).sort((a, b) => b.age - a.age);
    const wait = cards.filter((c) => !c.action).sort((a, b) => a.entry.stage - b.entry.stage || b.age - a.age);
    return { ...col, act, wait, count: cards.length };
  });
}

/** 営業リスト（段階0）: 進行中エントリーが無く、status が candidate のクリエイター */
export function listCandidates(creators: GfCreator[], entries: GfEntry[]): GfCreator[] {
  const busy = new Set(entries.filter((e) => !isArchived(e)).map((e) => e.influencer_id));
  return creators.filter((c) => c.status === "candidate" && !busy.has(c.id));
}

// ---- 案件タイプ・料率・条件 -------------------------------------------------------------

export const CAMPAIGN_TYPE_LABEL: Record<GfCampaignType, string> = {
  gift: "無償",
  paid: "有償",
  gift_affiliate: "無償＋アフィリ",
  paid_affiliate: "有償＋アフィリ",
};

export const CAMPAIGN_TYPES: readonly GfCampaignType[] = ["gift", "paid", "gift_affiliate", "paid_affiliate"];

export function typeHasFee(t: GfCampaignType): boolean {
  return t === "paid" || t === "paid_affiliate";
}

export function typeHasAffiliate(t: GfCampaignType): boolean {
  return t === "gift_affiliate" || t === "paid_affiliate";
}

export interface ResolvedRate {
  /** 小数（0.12）。アフィリ対象外/未登録は null */
  rate: number | null;
  label: string;
  src: "個別" | "案件" | "アフィリなし" | "未登録";
}

/**
 * 料率の解決（plan.md §0-3）。正本は affiliate の commission_rate。
 * アフィリ無し案件→「—」。affiliate 未登録→案件料率を「未登録」として表示（成果は計上されない）。
 * commission_rate ≠ base_rate → 個別、= → 案件。
 */
export function resolveRate(
  affiliate: Pick<AffAffiliate, "commission_rate"> | null | undefined,
  campaign: Pick<GfCampaign, "type" | "base_rate"> | null
): ResolvedRate {
  if (!campaign || !typeHasAffiliate(campaign.type)) return { rate: null, label: "—", src: "アフィリなし" };
  const base = campaign.base_rate ?? 0;
  if (!affiliate) return { rate: null, label: `${fmtPct(base)}（未登録）`, src: "未登録" };
  const r = affiliate.commission_rate;
  if (Math.abs(r - base) < 1e-9) return { rate: r, label: fmtPct(r), src: "案件" };
  return { rate: r, label: fmtPct(r), src: "個別" };
}

/** gifting influencer id → affiliate（gifting_influencer_id で突合） */
export function affiliateByInfluencer(affiliates: AffAffiliate[]): Map<string, AffAffiliate> {
  const m = new Map<string, AffAffiliate>();
  for (const a of affiliates) if (a.gifting_influencer_id && a.status !== "archived") m.set(a.gifting_influencer_id, a);
  return m;
}

/** カードの条件表記（例: ¥5万 ＋ 15%） */
export function termsLabel(e: Pick<GfEntry, "fee_amount" | "deal_type">, campaign: GfCampaign | null, rate: ResolvedRate): string {
  const parts: string[] = [];
  const fee = e.fee_amount ?? campaign?.fee_amount ?? 0;
  if (fee > 0 && (e.deal_type === "paid" || (campaign && typeHasFee(campaign.type)))) parts.push(fmtYenMan(fee));
  if (rate.rate !== null) parts.push(fmtPct(rate.rate));
  return parts.join(" ＋ ") || "—";
}

/** 試算: 固定報酬 + 想定売上 × 料率 */
export function estimatePayout(fee: number, rate: number | null, simSales: number): number {
  return Math.round(fee + (rate ?? 0) * simSales);
}

// ---- 整形 ---------------------------------------------------------------------------

export function fmtYen(n: number | null | undefined): string {
  return `¥${Math.round(n ?? 0).toLocaleString("ja-JP")}`;
}

/** ¥5万 / ¥12万 / ¥8,000（1万未満はそのまま） */
export function fmtYenMan(n: number): string {
  if (n >= 10_000 && n % 1000 === 0) {
    const man = n / 10_000;
    return `¥${Number.isInteger(man) ? man : man.toFixed(1)}万`;
  }
  return fmtYen(n);
}

/** ¥3.92M / ¥888K（KPI 用） */
export function fmtYenShort(n: number): string {
  if (n >= 1_000_000) return `¥${(n / 1_000_000).toFixed(2)}M`;
  if (n >= 10_000) return `¥${Math.round(n / 1000).toLocaleString("ja-JP")}K`;
  return fmtYen(n);
}

/** 0.12 → '12%'、0.085 → '8.5%' */
export function fmtPct(r: number): string {
  const v = Math.round(r * 1000) / 10;
  return `${Number.isInteger(v) ? v : v.toFixed(1)}%`;
}

/** フォロワー数 → '12.4万' / '9,800' */
export function fmtFollowers(n: number | null | undefined): string {
  if (n === null || n === undefined) return "—";
  if (n >= 10_000) {
    const v = Math.round(n / 1000) / 10;
    return `${Number.isInteger(v) ? v : v.toFixed(1)}万`;
  }
  return n.toLocaleString("ja-JP");
}

export const PLATFORM_SHORT: Record<GfPlatform, string> = {
  instagram: "IG",
  youtube: "YT",
  tiktok: "TT",
  ameblo: "AB",
  x: "X",
  threads: "TH",
  other: "他",
};

export const PLATFORM_LABEL: Record<GfPlatform, string> = {
  instagram: "Instagram",
  youtube: "YouTube",
  tiktok: "TikTok",
  ameblo: "Ameblo",
  x: "X",
  threads: "Threads",
  other: "その他",
};

/** 媒体チップ表記（Ameblo は PV 表記にしない＝データは follower_count のみのため '万' で統一） */
export function mediaChip(platform: GfPlatform, followers: number | null): string {
  return `${PLATFORM_SHORT[platform]} ${fmtFollowers(followers)}`;
}

/** プロフィール URL（url が無いときの推定） */
export function profileUrl(platform: GfPlatform, handle: string, url: string | null): string | null {
  if (url) return url;
  const h = handle.replace(/^@/, "");
  switch (platform) {
    case "instagram":
      return `https://www.instagram.com/${h}/`;
    case "youtube":
      return `https://www.youtube.com/@${h}`;
    case "tiktok":
      return `https://www.tiktok.com/@${h}`;
    case "ameblo":
      return `https://ameblo.jp/${h}/`;
    case "x":
      return `https://x.com/${h}`;
    case "threads":
      return `https://www.threads.net/@${h}`;
    default:
      return null;
  }
}

/** 主媒体（is_primary 優先→フォロワー最大） */
export function primarySns<T extends { is_primary: number; follower_count: number | null }>(sns: T[]): T | null {
  if (!sns.length) return null;
  return [...sns].sort((a, b) => b.is_primary - a.is_primary || (b.follower_count ?? -1) - (a.follower_count ?? -1))[0];
}

export function initialOf(name: string): string {
  const ch = Array.from(name.trim())[0] ?? "?";
  return ch.toUpperCase();
}

export function handleOf(sns: { handle: string } | null): string {
  if (!sns) return "";
  return sns.handle.startsWith("@") ? sns.handle : `@${sns.handle}`;
}

// ---- 2次利用 ------------------------------------------------------------------------

export const REUSE_STATE_LABEL: Record<GfReuseState, string> = {
  granted: "許諾済",
  expiring: "期限間近",
  expired: "期限切れ",
  requested: "申請中",
  none: "未取得",
};

/** reuse_allowed=1 → 許諾済（reuse_until 30日以内=期限間近、超過=期限切れ）／reuse_requested_at → 申請中／他=未取得 */
export function reuseState(
  p: Pick<GfPost, "reuse_allowed" | "reuse_until" | "reuse_requested_at">,
  nowIso: string
): GfReuseState {
  if (p.reuse_allowed === 1) {
    if (p.reuse_until) {
      const until = parseServerTime(p.reuse_until.length === 10 ? `${p.reuse_until}T23:59:59+09:00` : p.reuse_until);
      const now = parseServerTime(nowIso);
      if (until && now) {
        const left = until.getTime() - now.getTime();
        if (left < 0) return "expired";
        if (left <= 30 * DAY_MS) return "expiring";
      }
    }
    return "granted";
  }
  if (p.reuse_requested_at) return "requested";
  return "none";
}

export const REUSE_SCOPE_LABEL: Record<string, string> = {
  ad: "広告",
  ec: "EC商品ページ",
  sns: "SNS",
  rakuten_lp: "楽天LP",
  shopify_lp: "Shopify LP",
};

export function reuseScopeLabels(scope: string | null): string[] {
  if (!scope) return [];
  return scope
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean)
    .map((s) => REUSE_SCOPE_LABEL[s] ?? s);
}

// ---- DM テンプレ置換（旧SPA app.js fillTpl の移植） ----------------------------------------

export interface TplContext {
  name: string;
  product: string;
  formUrl: string | null;
  tokenRevoked: boolean;
  deadline: string | null;
}

export function fillTpl(body: string, ctx: TplContext): string {
  return body
    .replace(/\{名前\}/g, ctx.name)
    .replace(/\{商品\}/g, ctx.product)
    .replace(/\{フォームURL\}/g, ctx.formUrl && !ctx.tokenRevoked ? ctx.formUrl : "（フォームURLなし）")
    .replace(/\{期限\}/g, ctx.deadline ? ctx.deadline : "（期限未設定）");
}

/** 出荷内容の検証エラー（第2弾）。stoq-worker の検証文はもともと日本語なのでそのまま、gifting 側のコードだけ日本語にする */
const SHIP_ERROR_LABEL: Record<string, string> = {
  sku_missing: "SKU（商品コード）が未設定です。案件かエントリーに SKU を設定してください",
  not_configured: "倉庫出荷との連携が未設定です",
  scope_violation: "ギフティング以外の出荷は送れません",
  already_instructed: "この出荷指示は送信済みです",
  open_flag_block: "保留フラグがあるため送れません",
  invalid_json: "送信内容が不正です",
};

/** 案件にエントリーを追加できない理由（送る商品が未設定）。null なら追加できる */
export function campaignAddBlocker(
  c: (Pick<GfCampaign, "name" | "product_name"> & Partial<Pick<GfCampaign, "product_mode" | "product_options">>) | null | undefined
): string | null {
  if (!c) return null;
  if (c.product_mode === "choice") {
    return (c.product_options?.length ?? 0) > 0
      ? null
      : `案件「${c.name}」はクリエイターが選ぶセットが1つもありません。「報酬・料率」タブで選択肢を設定してください。`;
  }
  return c.product_name?.trim()
    ? null
    : `案件「${c.name}」の「送る商品」が未設定のため追加できません。「報酬・料率」タブでこの案件の送る商品（とSKU）を設定してください。`;
}

/** 選択待ち（choice 案件で未選択）のときの商品の言い方（DMテンプレの {商品} にも使う） */
export const PRODUCT_PENDING_LABEL = "STOQのセット（フォームでお選びいただけます）";

/** エントリーの「送る商品」の表示名。choice 案件は選ばれたセット名、未選択なら PRODUCT_PENDING_LABEL */
export function entryProductLabel(
  e: Pick<GfEntry, "product_name" | "product_pending">,
  c: Pick<GfCampaign, "product_name" | "product_mode"> | null | undefined
): string {
  if (e.product_pending) return PRODUCT_PENDING_LABEL;
  if (c?.product_mode === "choice") return e.product_name;
  return c?.product_name || e.product_name;
}

export function shipErrorText(errors: readonly string[]): string {
  return errors.map((e) => SHIP_ERROR_LABEL[e] ?? e).join(" / ") || "内容を確認してください";
}

/** Instagram DM の送信上限（gifting /api/ig/friends/:fid/messages と同じ） */
export const DM_MAX_CHARS = 1000;

/** DM 送信に使える Instagram アカウント（ig-harness のフォロワーに紐付け済みのもの）。無ければ null */
export function linkedIgOf<S extends Pick<GfSns, "platform" | "ig_follower_id">>(sns: S[]): (S & { ig_follower_id: number }) | null {
  const s = sns.find((x) => x.platform === "instagram" && x.ig_follower_id != null);
  return s ? (s as S & { ig_follower_id: number }) : null;
}

// ---- 代理入力の住所分割 ---------------------------------------------------------------

export interface ParsedAddress {
  postal: string;
  address: string;
  phone: string;
  name: string;
}

/** 全角英数→半角 */
export function toHalfWidth(s: string): string {
  return s
    .replace(/[０-９Ａ-Ｚａ-ｚ]/g, (c) => String.fromCharCode(c.charCodeAt(0) - 0xfee0))
    .replace(/[－−ー―‐]/g, (c) => (c === "ー" ? c : "-"));
}

/** 郵便番号を 7桁数字に（不正なら ''） */
export function normalizePostal(s: string): string {
  const d = toHalfWidth(s).replace(/[〒\s-]/g, "");
  return /^\d{7}$/.test(d) ? d : "";
}

/** 電話番号を 0始まり10〜11桁に（+81 対応・不正なら ''） */
export function normalizePhone(s: string): string {
  let d = toHalfWidth(s).replace(/[\s()-]/g, "");
  if (d.startsWith("+81")) d = "0" + d.slice(3).replace(/^0/, "");
  return /^0\d{9,10}$/.test(d) ? d : "";
}

/**
 * 貼り付けテキストから 郵便番号・住所・電話・氏名 を推定分割する（DM の文面想定）。
 * 判定できない部分は空文字（UI で手直しする前提）。
 */
export function parsePastedAddress(text: string): ParsedAddress {
  const src = toHalfWidth(text);
  // 電話番号を先に取り除いてから郵便番号を探す（090-1234-5678 の先頭を郵便番号と誤認しないため）
  const phoneM = src.match(/(\+81[\d\s-]{9,14}\d|0\d{1,4}-?\d{1,4}-?\d{3,4})/g);
  const phoneRaw = phoneM ? phoneM.find((p) => normalizePhone(p)) ?? "" : "";
  const phone = phoneRaw ? normalizePhone(phoneRaw) : "";
  const noPhone = phoneRaw ? src.split(phoneRaw).join("\n") : src;
  const postalM = noPhone.match(/〒\s*(\d{3})-?(\d{4})/) ?? noPhone.match(/(?<![\d-])(\d{3})-?(\d{4})(?![\d-])/);
  const postal = postalM ? `${postalM[1]}${postalM[2]}` : "";
  const postalRaw = postalM ? postalM[0] : "";
  const lines = noPhone
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter(Boolean)
    .map((l) => (postalRaw ? l.replace(postalRaw, "") : l).replace(/^(住所|電話番号?|TEL|tel|お名前|氏名|名前)\s*[:：]?\s*/, "").trim())
    .filter(Boolean);
  const PREF = /(北海道|東京都|大阪府|京都府|.{2,3}県)/;
  const addrIdx = lines.findIndex((l) => PREF.test(l));
  // 住所の次の行は「建物名・部屋番号らしい（数字/号/階/室を含み、様で終わらない）」ときだけ住所に含める
  const next = addrIdx >= 0 ? lines[addrIdx + 1] : undefined;
  const nextIsBuilding = !!next && /[\d号階室棟]/.test(next) && !/(様|さま)$/.test(next) && !PREF.test(next);
  const addrLines = addrIdx >= 0 ? [addrIdx, ...(nextIsBuilding ? [addrIdx + 1] : [])] : [];
  const address = addrLines.map((i) => lines[i]).join(" ").trim();
  const nameLine = lines.find((l, i) => !addrLines.includes(i) && !PREF.test(l) && l.length <= 20);
  const name = nameLine ? nameLine.replace(/(様|さま)$/, "").trim() : "";
  return { postal, address, phone, name };
}

// ---- 振込照合用 CSV -----------------------------------------------------------------

export interface PayoutCsvRow {
  name: string;
  holderKana: string;
  bank: string;
  branch: string;
  accountType: string;
  last4: string;
  invoice: string;
  fixed: number;
  affiliate: number;
}

function csvCell(v: string | number): string {
  const s = String(v);
  return /[",\r\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

/** 照合用CSV（全銀形式ではない）。UTF-8 BOM 付きで Excel でも文字化けしない */
export function buildPayoutCsv(rows: PayoutCsvRow[], month: string): string {
  const head = ["対象月", "支払先", "口座名義(カナ)", "銀行", "支店", "種別", "口座下4桁", "インボイス", "固定報酬", "アフィリ報酬", "支払額"];
  const body = rows.map((r) =>
    [month, r.name, r.holderKana, r.bank, r.branch, r.accountType, r.last4, r.invoice, r.fixed, r.affiliate, r.fixed + r.affiliate]
      .map(csvCell)
      .join(",")
  );
  return "﻿" + [head.join(","), ...body].join("\r\n") + "\r\n";
}

// ---- 第3弾: Instagram 投稿の自動検知・2次利用の依頼DM自動送信 ------------------------------------

/** 自動送信に使う既定テンプレ（案件の reuse_dm_template_id が null のとき。gifting 側の既定と同じ） */
export const DEFAULT_REUSE_DM_TEMPLATE_ID = "tpl-投稿お礼";

/** 精算・連携タブの「Instagram 投稿検知」カード（発送の OMS カードと同じ作り）。status が取れた後の表示 */
export function igDetectCardOf(s: GfIgDetectStatus): { tone: "ok" | "warn"; label: string; detail: string } {
  if (s.last_error) return { tone: "warn", label: "確認エラー", detail: `直近のエラー: ${s.last_error}` };
  return {
    tone: "ok",
    label: "接続済み",
    detail: `最終確認 ${s.last_sync_at ? fmtDateTime(s.last_sync_at) : "未実行"}・直近7日の自動記録 ${s.counts.linked_7d}件`,
  };
}

/** 検知した投稿の件数内訳（ダッシュボード要対応・素材タブのバッジ用）。review=クリエイター登録済みで案件に紐づいていない／sales=未登録アカウント（営業候補） */
export function igDetectCounts(media: readonly Pick<GfIgDetectedMedia, "status">[]): { review: number; sales: number; total: number } {
  const review = media.filter((m) => m.status === "creator_no_deal").length;
  const sales = media.filter((m) => m.status === "unknown").length;
  return { review, sales, total: review + sales };
}

/** ダッシュボード要対応「検知した投稿の確認 n件」（creator_no_deal のみ。unknown は件数だけ添える）。0件なら null */
export function igDetectTodoOf(media: readonly Pick<GfIgDetectedMedia, "status">[]): { title: string; sub: string; count: number } | null {
  const c = igDetectCounts(media);
  if (c.review === 0) return null;
  return {
    title: `検知した投稿の確認 ${c.review}件`,
    sub: c.sales > 0 ? `案件に紐づけてください（ほか未登録アカウント ${c.sales}件は営業候補）` : "案件に紐づけてください（素材タブ）",
    count: c.review,
  };
}

/** 検知した投稿を紐づけられる案件（そのクリエイターの段階4〜7・中止でない）。段階5（投稿待ち）を先頭に、あとは新しい更新順 */
export function dealsForMedia(
  entries: readonly Pick<GfEntry, "id" | "influencer_id" | "stage" | "status" | "updated_at">[],
  influencerId: string | null
): Array<Pick<GfEntry, "id" | "influencer_id" | "stage" | "status" | "updated_at">> {
  if (!influencerId) return [];
  return entries
    .filter((e) => e.influencer_id === influencerId && e.status !== "cancelled" && e.stage >= 4 && e.stage <= 7)
    .sort((a, b) => (a.stage === 5 ? 0 : 1) - (b.stage === 5 ? 0 : 1) || b.updated_at.localeCompare(a.updated_at));
}

/** 段階6→7 の自動DMの結果をトースト用の1文に。auto_dm が無い（対象外）なら null */
export function autoDmMessage(a: GfAutoDm | null | undefined): string | null {
  if (!a) return null;
  if (a.sent) return "2次利用の依頼DMを自動で送りました";
  switch (a.reason) {
    case "not_linked":
      return "Instagram と未連携のため、2次利用の依頼DMは送れませんでした。手動で依頼してください";
    case "not_configured":
      return "自動送信の設定が未完了のため、2次利用の依頼DMは送れませんでした。手動で依頼してください";
    default:
      return "2次利用の依頼DMの自動送信に失敗しました。履歴を確認し、手動で送ってください（24時間を過ぎると自動送信できません）";
  }
}

/** 自動DMの対象か（案件で自動送信がONかつ2次利用あり）。段階6→7 の確認ダイアログの出し分けに使う */
export function willAutoSendReuseDm(c: Pick<GfCampaign, "reuse_required" | "reuse_dm_auto"> | null | undefined): boolean {
  return !!c && c.reuse_required === 1 && c.reuse_dm_auto === 1;
}
